import React, { FunctionComponent, ReactNode, SVGProps } from 'react';
import Typography from '@mui/material/Typography';
import { Box, SxProps } from '@mui/system';
import SvgIcon from '@mui/material/SvgIcon';
import { useSidebarNavigation } from '@/contexts/sidebarNavigationContext';
import { LaunchOutlined as LaunchIcon, SvgIconComponent } from '@mui/icons-material';
import { Button, Tooltip, Zoom } from '@mui/material';
import StyledLink from '@/components/styled/StyledLink';

export interface SidebarLinkProps {
  to?: string | undefined;
  icon: FunctionComponent<SVGProps<SVGSVGElement>> | SvgIconComponent;
  text?: string;
  boxContainerStyles?: SxProps;
  target?: string | undefined;
  handleClick?: (() => void) | string | undefined;
  isVisible?: boolean;
  isExternal?: boolean;
  subMenu?: boolean;
  /** drawn on the icon's top-right corner (the bell's unread count), the same at 65px and wide */
  iconBadge?: ReactNode;
}

const SidebarLink: React.FC<SidebarLinkProps> = ({
  to = '',
  icon,
  text,
  boxContainerStyles,
  target,
  handleClick,
  isExternal,
  subMenu,
  iconBadge,
}) => {
  const { sidebarStyle } = useSidebarNavigation();

  return (
    <Box
      sx={{
        ...boxContainerStyles,
        button: { padding: '0' },
        ...(sidebarStyle === 'mini' && { paddingLeft: '8px' }),
        ...(isExternal && { paddingRight: '20px' }),
        position: 'relative',
        '&:hover .icon-external, &:focus .icon-external': {
          opacity: '1',
        },
      }}
    >
      <LinkOrButton to={to} target={target} handleClick={handleClick}>
        {subMenu && <Typography sx={{ width: '20px' }}></Typography>}
        {iconBadge ? (
          <Box
            component='span'
            sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}
          >
            <SvgIcon component={icon} inheritViewBox sx={{ color: 'typography.main' }} />
            {iconBadge}
          </Box>
        ) : (
          <SvgIcon component={icon} inheritViewBox sx={{ color: 'typography.main' }} />
        )}

        {text && (
          // a long text (a role's name) ends in "…" inside the sidebar instead of running out of it
          <Typography
            title={text}
            sx={{
              textWrap: 'nowrap',
              color: 'typography.main',
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            textTransform='capitalize'
          >
            {text}
          </Typography>
        )}

        {isExternal && (
          <Box
            className='icon-external'
            sx={{
              position: 'absolute',
              right: '0',
              transition: 'opacity .3s',
              opacity: '0',
            }}
          >
            <Tooltip
              placement='top'
              TransitionComponent={Zoom}
              title='This item will open in a new tab'
            >
              <LaunchIcon sx={{ color: 'typography.main', opacity: '.8' }} fontSize='small' />
            </Tooltip>
          </Box>
        )}
      </LinkOrButton>
    </Box>
  );
};

interface LinkOrButtonProps {
  to: string;
  target?: string;
  handleClick?: (() => void) | string | undefined;
  children: ReactNode;
}

export const LinkOrButton = ({ to, target, handleClick, children }: LinkOrButtonProps) => {
  if (to && to !== 'javascript:void(0);') {
    return (
      <StyledLink to={to} target={target}>
        {children}
      </StyledLink>
    );
  }

  return (
    <Button
      variant='text'
      onClick={typeof handleClick === 'function' ? handleClick : undefined}
      sx={{ color: 'typography.main', maxWidth: '100%', minWidth: 0 }}
    >
      {children}
    </Button>
  );
};

export default SidebarLink;
